import type { Components } from "react-markdown";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import { NavLink } from "react-router-dom";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { parseDocHeading } from "../toc";

function childrenToText(children: ReactNode): string {
  if (children == null || typeof children === "boolean") return "";
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join("");
  if (typeof children === "object" && "props" in children) {
    const props = (children as { props?: { children?: ReactNode } }).props;
    return childrenToText(props?.children);
  }
  return "";
}

export function useDocsMarkdownComponents(): Components {
  const docs = useAppCustomStyles().pages.docs;

  const heading =
    (Tag: "h1" | "h2" | "h3" | "h4") =>
    ({ children }: { children?: ReactNode }) => {
      const raw = childrenToText(children).trim();
      const { id } = parseDocHeading(raw);
      // Hide a trailing `{#id}` marker from the visible heading.
      const visible = Array.isArray(children)
        ? children.map((c) => (typeof c === "string" ? c.replace(/\s*\{#[A-Za-z0-9_-]+\}\s*$/, "") : c))
        : typeof children === "string"
          ? children.replace(/\s*\{#[A-Za-z0-9_-]+\}\s*$/, "")
          : children;
      return (
        <Box component={Tag} id={raw ? id : undefined} sx={{ scrollMarginTop: 96 }}>
          {visible}
        </Box>
      );
    };

  return {
    h1: heading("h1"),
    h2: heading("h2"),
    h3: heading("h3"),
    h4: heading("h4"),
    a: ({ href, children }) => {
      if (href?.startsWith("/docs/") || href === "/release-notes") {
        return (
          <Box component={NavLink} to={href} sx={{ color: "primary.main" }}>
            {children}
          </Box>
        );
      }
      if (href?.startsWith("#")) {
        return (
          <Box component="a" href={href} sx={{ color: "primary.main" }}>
            {children}
          </Box>
        );
      }
      return (
        <Box
          component="a"
          href={href}
          target={href?.startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
        >
          {children}
        </Box>
      );
    },
    img: ({ src, alt }) => (
      <Box component="img" src={src} alt={alt ?? ""} sx={docs.markdownImage} />
    ),
    table: ({ children }) => (
      <Box sx={docs.markdownTableWrap} component="div">
        <table>{children}</table>
      </Box>
    ),
    pre: ({ children }) => <Box component="pre" sx={docs.markdownPre}>{children}</Box>,
    code: ({ children, className }) => {
      const isBlock = Boolean(className);
      if (isBlock) {
        return <code className={className}>{children}</code>;
      }
      return (
        <Box component="code" sx={docs.markdownCode}>
          {children}
        </Box>
      );
    },
  };
}
